import React, { useState, useRef, useEffect } from "react";
import patientService from "../../../Services/patientService";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import doctorService from "../../../Services/doctorService";
import saleService from "../../../Services/saleService";
import { pdf } from "@react-pdf/renderer";
import PatientReport from "../PatientReport";

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[500] text-gray-400 mb-3 mt-6">
    {children}
  </p>
);

const InputField = ({ label, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <input
      {...props}
      className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none shadow-sm hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
    />
  </div>
);

const DoctorSelect = ({ value, onChange, doctorData }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selected = doctorData.find((d) => d.id === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="flex flex-col gap-1">
      <label className="text-[12px] font-[500] text-gray-500">Select Doctor</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className={`w-full px-3 py-2 text-[13px] text-left bg-white border rounded-lg outline-none shadow-sm transition-colors flex items-center justify-between
            hover:border-orange-200
            ${open ? "border-orange-200 ring-2 ring-orange-100" : "border-gray-200"}
            ${value ? "text-gray-800" : "text-gray-400"}`}
        >
          <span className="truncate">{selected ? selected.name : "Choose a doctor"}</span>
          <svg
            className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <div className="max-h-48 overflow-y-auto scroll-smooth">
              {doctorData.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => {
                    onChange({ target: { name: "doc_id", value: doc.id } });
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-[13px] text-left transition-colors
                    hover:bg-orange-50 hover:text-orange-600
                    ${value === doc.id ? "bg-orange-50 text-orange-600 font-[500]" : "text-gray-700"}`}
                >
                  <span className="font-[500]">{doc.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddEditCheckup = () => {
  const [checkupData, setCheckupData] = useState({
    patient_id: "",
    doc_id: "",
    fee: "",
    doctor_commission: "",
    hospital_tax: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [patientData, setPatientData] = useState([]);
  const [doctorData, setDoctorData] = useState([]);
  const [selectedPatientName, setSelectedPatientName] = useState("");
  const [selectedDoctorName, setSelectedDoctorName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchData, setSearchData] = useState("");
  const [latestPatient, setLatestPatient] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLatestPatient, setShowLatestPatient] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const [loggedUser, setLoggedUser] = useState({ id: "", name: "" });

  useEffect(() => {
    const uid = localStorage.getItem("user_id");
    const name = localStorage.getItem("user_name");
    if (uid && name) setLoggedUser({ id: uid, name });
  }, []);

  useEffect(() => {
    if (id) {
      const fetchPatientData = async () => {
        try {
          const response = await patientService.fetchPatientById(id);
          setCheckupData(response.patient);
        } catch (error) {
          toast.error("Error fetching patient data.");
        }
      };
      fetchPatientData();
    }
  }, [id]);

  useEffect(() => {
    const getPatients = async () => {
      try {
        const response = await patientService.fetchAllPatients();
        setPatientData(response.patients);
        if (response.patients.length > 0) {
          const latest = response.patients.reduce((prev, current) =>
            prev.patient_id > current.patient_id ? prev : current
          );
          setLatestPatient(latest);
        }
      } catch (error) {
        toast.error("Error fetching Patients");
      }
    };
    getPatients();
  }, []);

  useEffect(() => {
    const getSales = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllSale();
        setSales(response.sales);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getSales();
  }, []);

  useEffect(() => {
    if (latestPatient && sales.length > 0) {
      const isPatientInSales = sales.some(
        (sale) => sale.patient_id === latestPatient.patient_id
      );
      setShowLatestPatient(!isPatientInSales);
    }
  }, [latestPatient, sales]);

  useEffect(() => {
    const getDoctors = async () => {
      try {
        const response = await doctorService.fetchAllDoctors();
        setDoctorData(response.doctors);
      } catch (error) {
        toast.error("Error fetching Doctors");
      }
    };
    getDoctors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCheckupData((prev) => ({ ...prev, [name]: value }));

    if (name === "patient_id") {
      const sel = patientData.find((p) => p.patient_id === value);
      setSelectedPatientName(sel?.full_name || "");
    }

    if (name === "doc_id") {
      const sel = doctorData.find((d) => d.id === value);
      setSelectedDoctorName(sel?.name || "");
      setCheckupData((prev) => ({
        ...prev,
        doc_id: value,
        fee: sel?.fee || "",
        doctor_commission: sel?.commission_fee ?? 0,
        hospital_tax: sel?.hospital_tax ?? 0,
      }));
    }
  };

  const handleFormSubmit = async () => {
    try {
      const payload = { ...checkupData, user_id: loggedUser.id };
      let saleId, saleUniqueId;

      if (id) {
        await saleService.update(id, payload);
        toast.success("Checkup updated successfully!", {
          style: { borderRadius: "10px", background: "#1e1b2e", color: "#fff", fontSize: "13px" },
        });
        saleId = id;
      } else {
        const result = await saleService.create(payload); // { id, sale_unique_id }
        saleId = result?.id;
        saleUniqueId = result?.sale_unique_id;
        toast.success("Checkup saved successfully!", {
          style: { borderRadius: "10px", background: "#1e1b2e", color: "#fff", fontSize: "13px" },
        });
      }
      return { saleId, saleUniqueId };
    } catch (error) {
      toast.error("Error saving details.", {
        style: { borderRadius: "10px", background: "#1e1b2e", color: "#fff", fontSize: "13px" },
      });
      return { saleId: null, saleUniqueId: null };
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!checkupData.patient_id) {
      toast.error("Please select a patient", {
        icon: "⚠️",
        style: { borderRadius: "10px", background: "#1e1b2e", color: "#fff", fontSize: "13px" },
      });
      return;
    }
    if (!checkupData.doc_id) {
      toast.error("Please select a doctor", {
        icon: "⚠️",
        style: { borderRadius: "10px", background: "#1e1b2e", color: "#fff", fontSize: "13px" },
      });
      return;
    }

    if (selectedPatient) setSelectedPatientName(selectedPatient.full_name);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => setIsDialogOpen(false);

  const handlePrint = async () => {
    try {
      setIsDialogOpen(false);
      const { saleId, saleUniqueId } = await handleFormSubmit();

      const selectedDoctor = doctorData.find((d) => d.id === checkupData.doc_id);
      const selectedPatientFull = patientData.find(
        (p) => p.patient_id === checkupData.patient_id
      );

      const patientPayload = {
        patient_name: selectedPatientName,
        doctor_name: selectedDoctorName,
        pmdc_no: selectedDoctor?.pmdc_no || "",
        patient_id: checkupData.patient_id,
        sale_unique_id: saleUniqueId || "",
        checkup_date: checkupData.date || new Date().toISOString().split("T")[0],
        qualification: selectedDoctor?.qualification || "",
        specialization: selectedDoctor?.specialization || "",
        age: selectedPatientFull?.age || "",
        date_of_birth: selectedPatientFull?.date_of_birth || "",
        gender: selectedPatientFull?.gender || "",
        history: "",
        diagnosis: "",
      };

      const doc = <PatientReport patient={patientPayload} />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      const newWindow = window.open(url, "_blank");
      if (newWindow) {
        newWindow.onload = () => {
          newWindow.focus();
          newWindow.print();
        };
      }
    } catch (error) {
      console.error(error);
      toast.error("Print failed");
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchData("");
    setCheckupData((prev) => ({ ...prev, patient_id: patient.patient_id }));
    setSelectedPatientName(patient.full_name || "");
  };

  return (
  <div className="p-3 sm:p-6">
    <div className="flex flex-col gap-5">
        {showLatestPatient && latestPatient && (
          <div className="bg-white border w-full sm:w-[60%] md:w-[40%] border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-[#215187] px-4 sm:px-6 py-3 flex items-center justify-between">
              <p className="text-white text-[13px] font-[500] tracking-tight">
                Recently Registered Patient
              </p>
            </div>
            <div className="px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="flex flex-col gap-0.5">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-[500]">Patient Name</p>
                <p className="text-[13px] font-[500] text-gray-800">{latestPatient.full_name}</p>
              </div>
              <div className="flex flex-col gap-0.5">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-[500]">Contact</p>
                <p className="text-[13px] font-[500] text-gray-800">{latestPatient.contact_number}</p>
              </div>
              <div className="flex flex-col gap-0.5">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-[500]">Patient ID</p>
                <span className="inline-flex w-fit bg-blue-50 text-blue-600 text-[11px] font-[500] px-2.5 py-0.5 rounded-full">
                  #{latestPatient.patient_id}
                </span>
              </div>
            </div>
            <div className="px-4 sm:px-6 pb-4">
              <button
                type="button"
                onClick={() => handlePatientSelect(latestPatient)}
                className="text-[12px] font-[500] text-orange-500 hover:text-orange-600 transition-colors"
              >
                + Select this patient →
              </button>
            </div>
          </div>
        )}

      
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

         
          <div className="bg-[#215187] px-4 sm:px-8 py-4 sm:py-5">
            <h1 className="text-white text-[16px] sm:text-[18px] font-[500] tracking-tight">
              {id ? "Edit Checkup" : "New Checkup Details"}
            </h1>
            <p className="text-gray-400 text-[12px] mt-0.5">
              {id
                ? "Update the checkup information below"
                : "Search for a patient and assign a doctor to proceed"}
            </p>
          </div>

          
          <form onSubmit={handleSubmit} className="px-4 sm:px-8 py-5 sm:py-6">

            <SectionLabel>Patient & Doctor</SectionLabel>

           
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-[500] text-gray-500">Search Patient</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or patient ID..."
                    value={searchData || selectedPatient?.full_name || ""}
                    onChange={(e) => {
                      setSearchData(e.target.value);
                      if (selectedPatient) setSelectedPatient(null);
                    }}
                    className="w-full px-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none shadow-sm hover:border-orange-200 focus:border-orange-200 focus:ring-2 focus:ring-orange-100"
                  />
                  {searchData && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                      
                      <div className="hidden xs:grid grid-cols-3 px-3 py-2 bg-gray-50 border-b border-gray-100">
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-[500]">ID</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-[500]">Name</p>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-[500]">Phone</p>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {patientData
                          .filter(
                            (p) =>
                              p.full_name?.toLowerCase().includes(searchData.toLowerCase()) ||
                              p.patient_id?.toString().includes(searchData)
                          )
                          .slice(0, 10)
                          .map((patient) => (
                            <button
                              key={patient.patient_id}
                              type="button"
                              onClick={() => handlePatientSelect(patient)}
                              className="w-full px-3 py-2 text-left hover:bg-orange-50 border-b border-gray-50 transition-colors"
                            >
                      
                              <div className="flex items-center justify-between sm:hidden">
                                <div>
                                  <p className="text-[12px] text-gray-700 font-[500]">{patient.full_name}</p>
                                  <p className="text-[11px] text-gray-400">{patient.contact_number}</p>
                                </div>
                                <span className="text-[11px] text-blue-500 font-[500]">#{patient.patient_id}</span>
                              </div>
                           
                              <div className="hidden sm:grid grid-cols-3">
                                <span className="text-[12px] text-blue-500 font-[500]">#{patient.patient_id}</span>
                                <span className="text-[12px] text-gray-700">{patient.full_name}</span>
                                <span className="text-[12px] text-gray-500">{patient.contact_number}</span>
                              </div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DoctorSelect
                value={checkupData.doc_id}
                onChange={handleChange}
                doctorData={doctorData}
              />
            </div>

            <SectionLabel>Checkup Info</SectionLabel>


            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-[500] text-gray-500">Checkup Date</label>
                <div className="w-full px-3 py-2 text-[13px] text-gray-800 bg-gray-50 border border-gray-200 rounded-lg shadow-sm cursor-not-allowed">
                  <input
                    type="date"
                    name="date"
                    value={checkupData.date}
                    onChange={handleChange}
                    required
                    readOnly
                    className="w-full outline-none bg-transparent cursor-not-allowed text-gray-500"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-[500] text-gray-500 flex flex-wrap items-center gap-1">
                  Checkup Fee
                  <span className="text-[10px] text-orange-400 normal-case">(auto-filled from doctor)</span>
                </label>
                <input
                  type="number"
                  name="fee"
                  value={checkupData.fee}
                  onChange={handleChange}
                  required
                  readOnly
                  placeholder="Select a doctor to fill fee"
                  className="w-full px-3 py-2 text-[13px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg outline-none shadow-sm cursor-not-allowed"
                />
              </div>
            </div>

 
            <div className="border-t border-gray-100 mt-8 pt-5 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500] text-center sm:text-left py-2 sm:py-0"
              >
                ← Cancel
              </button>
              <button
                type="submit"
                className="bg-[#2F6DB4] hover:bg-[#255A8C] text-white transition-colors text-[13px] font-[500] px-6 py-2.5 rounded-lg w-full sm:w-auto"
              >
                {id ? "Update Checkup" : "Save & Continue →"}
              </button>
            </div>

          </form>
        </div>


        {isDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-20 px-3 sm:px-0">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-md overflow-hidden mb-0 sm:mb-auto">

        
              <div className="bg-[#215187] px-5 sm:px-6 py-4 sm:py-5">
                <div className="flex items-center gap-2.5 mb-1">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="1" width="14" height="14" rx="3" stroke="white" strokeWidth="1.2" />
                    <path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <h2 className="text-white text-[14px] font-[500]">Confirm Submission</h2>
                </div>
                <p className="text-white/40 text-[12px] ml-[26px]">Please review the details before confirming</p>
              </div>

          
              <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-2 flex flex-col gap-0.5">
                {[
                  {
                    label: "Patient",
                    value: selectedPatientName,
                    bg: "bg-[#EEEDFE]",
                    icon: (
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="5.5" r="3" stroke="#534AB7" strokeWidth="1.3" />
                        <path d="M2 13.5c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="#534AB7" strokeWidth="1.3" strokeLinecap="round" />
                      </svg>
                    ),
                    rowBg: "bg-gray-50",
                  },
                  {
                    label: "Doctor",
                    value: selectedDoctorName,
                    bg: "bg-[#E1F5EE]",
                    icon: (
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <rect x="3" y="2" width="10" height="12" rx="2" stroke="#0F6E56" strokeWidth="1.3" />
                        <path d="M6 6h4M6 9h2.5" stroke="#0F6E56" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                    ),
                    rowBg: "",
                  },
                  {
                    label: "Consultation fee",
                    value: `Rs. ${checkupData.fee}`,
                    bg: "bg-[#FAEEDA]",
                    icon: (
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="5.5" stroke="#854F0B" strokeWidth="1.3" />
                        <path d="M8 5v3l2 1.5" stroke="#854F0B" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                    ),
                    rowBg: "bg-gray-50",
                  },
                  {
                    label: "Date",
                    value: checkupData.date,
                    bg: "bg-[#E6F1FB]",
                    icon: (
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                        <rect x="2" y="3.5" width="12" height="10" rx="1.5" stroke="#185FA5" strokeWidth="1.3" />
                        <path d="M5 2v3M11 2v3M2 7.5h12" stroke="#185FA5" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                    ),
                    rowBg: "",
                  },
                ].map(({ label, value, bg, icon, rowBg }) => (
                  <div
                    key={label}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg ${rowBg}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
                        {icon}
                      </div>
                      <span className="text-[11px] text-gray-400 font-[500] uppercase tracking-wider">{label}</span>
                    </div>
                    <span className="text-[13px] text-gray-800 font-[500] text-right max-w-[55%] truncate">{value}</span>
                  </div>
                ))}
              </div>

             
              <div className="px-4 sm:px-5 py-4 mt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                <button
                  onClick={handleDialogClose}
                  className="flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-gray-700 transition-colors font-[500]"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Cancel
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 bg-[#2F6DB4] hover:bg-[#255A8C] text-white transition-colors text-[13px] font-[500] px-4 sm:px-5 py-2.5 rounded-lg"
                >
                  Confirm & save
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3.5 8h9M9 4.5L12.5 8 9 11.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AddEditCheckup;